import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { UserService } from '../../services/api';
import { Skill } from '../../types';
import toast from 'react-hot-toast';
import SkillsAutocomplete from '../../components/profile/SkillsAutocomplete';
import AvailabilityCalendar from '../../components/profile/AvailabilityCalendar';

// Added availability interface locally since it's not exported from types
interface TimeSlot {
  label: string;
  value: string;
  start: string;
  end: string;
  available: boolean;
}

interface DayAvailability {
  date: string;
  timeSlots: TimeSlot[];
}

// Custom CSS for animations and responsive design
const customStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fadeInDelay {
    0% { opacity: 0; transform: translateY(10px); }
    50% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fadeInDelay2 {
    0% { opacity: 0; transform: translateY(10px); }
    70% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes scaleIn {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.5s ease-out forwards;
  }
  
  .animate-fade-in-delay {
    animation: fadeInDelay 1s ease-out forwards;
  }
  
  .animate-fade-in-delay-2 {
    animation: fadeInDelay2 1.5s ease-out forwards;
  }
  
  .animate-scale-in {
    animation: scaleIn 0.5s ease-out forwards;
  }
  
  /* Enhanced Mobile responsive styles */
  @media (max-width: 640px) {
    .onboarding-container {
      padding: 0.5rem;
      margin: 0 auto;
    }
    
    .onboarding-card {
      padding: 1.25rem;
      border-radius: 1.25rem;
      margin: 0.5rem;
    }
    
    .onboarding-title {
      font-size: 1.5rem;
      line-height: 1.75rem;
    }
    
    .onboarding-subtitle {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
    
    .step-indicator {
      width: 2rem;
      height: 2rem;
      font-size: 0.875rem;
    }
    
    .avatar-preview {
      width: 6rem;
      height: 6rem;
    }
    
    .navigation-buttons {
      flex-direction: column;
      gap: 0.75rem;
    }
    
    .nav-button {
      width: 100%;
      justify-content: center;
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
    }
    
    .skill-tag {
      font-size: 0.75rem;
      padding: 0.25rem 0.75rem;
    }
    
    .grid-cols-2 {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .property-type-card, .project-type-card {
      min-width: auto;
      padding: 0.75rem;
    }
    
    /* Adjust form elements for small screens */
    .input, .textarea, .select {
      font-size: 16px; /* Prevents zoom on iOS */
      padding: 0.625rem;
    }
    
    /* Adjust spacing for mobile */
    .space-y-8 > *:not(:first-child) {
      margin-top: 1.5rem;
    }
    
    .space-y-6 > *:not(:first-child) {
      margin-top: 1rem;
    }
    
    /* Adjust button sizes */
    .btn-mobile {
      padding: 0.625rem 1rem;
      font-size: 0.875rem;
    }
    
    /* Adjust profile summary */
    .profile-summary-item {
      font-size: 0.875rem;
    }
  }
  
  /* Extra small devices */
  @media (max-width: 400px) {
    .onboarding-card {
      padding: 1rem;
    }
    
    .onboarding-title {
      font-size: 1.25rem;
    }
    
    .avatar-preview {
      width: 5rem;
      height: 5rem;
    }
    
    .step-indicator {
      width: 1.75rem;
      height: 1.75rem;
      font-size: 0.75rem;
    }
  }
  
  /* Medium devices */
  @media (min-width: 641px) and (max-width: 768px) {
    .onboarding-container {
      padding: 1rem;
    }
    
    .onboarding-card {
      padding: 1.5rem;
    }
  }
  
  /* Prevent horizontal scrolling */
  .onboarding-container {
    max-width: 100%;
    overflow-x: hidden;
  }
  
  /* Ensure form elements don't overflow */
  .onboarding-card input,
  .onboarding-card textarea,
  .onboarding-card select {
    max-width: 100%;
  }
  
  /* Adjust grid for different screen sizes */
  @media (max-width: 576px) {
    .sm\:grid-cols-2 {
      grid-template-columns: 1fr;
    }
  }
`;

const OnboardingPage: React.FC = () => {
  // Inject custom styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);
  
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  
  // Onboarding steps
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = user?.role === 'TRADESPERSON' ? 6 : 5; // Added availability step for tradespeople
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    bio: '',
    phoneNumber: '',
    address: '',
    city: '',
    postcode: '',
    serviceRadius: 25,
    yearsExperience: '',
    hourlyRate: '',
    skillsOffered: [] as Skill[],
    isInsured: false,
    homeownerPreferences: {
      propertyTypes: [] as string[],
      budgetRange: { min: 0, max: 5000 },
      projectTypes: [] as string[],
    },
    availability: [] as DayAvailability[], // Added availability for tradespeople
  });
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Initialize with user data if available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        companyName: user.profile?.companyName || '',
        bio: user.profile?.bio || '',
        phoneNumber: user.profile?.phoneNumber || '',
        address: user.profile?.address || '',
        city: user.profile?.city || '',
        postcode: user.profile?.postcode || '',
        serviceRadius: user.profile?.serviceRadius || 25,
        yearsExperience: user.profile?.yearsExperience?.toString() || '',
        hourlyRate: user.profile?.hourlyRate?.toString() || '',
        skillsOffered: user.profile?.skillsOffered || [],
        isInsured: user.profile?.isInsured || false,
        availability: [] as DayAvailability[], // Will be set when user provides availability
      }));
    }
  }, [user]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };

  // Handle skills change
  const handleSkillsChange = (skills: Skill[]) => {
    setFormData(prev => ({
      ...prev,
      skillsOffered: skills
    }));
  };

  // Handle availability change
  const handleAvailabilityChange = (availability: DayAvailability[]) => {
    setFormData(prev => ({
      ...prev,
      availability: availability
    }));
  };

  // Handle avatar file selection
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle homeowner preferences change
  const handlePreferencesChange = (preferences: any) => {
    setFormData(prev => ({
      ...prev,
      homeownerPreferences: preferences
    }));
  };

  // Navigation between steps
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare profile data
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        bio: formData.bio,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        city: formData.city,
        postcode: formData.postcode,
        serviceRadius: formData.serviceRadius,
        yearsExperience: formData.yearsExperience ? parseInt(formData.yearsExperience) : undefined,
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
        skillsOffered: formData.skillsOffered,
        isInsured: formData.isInsured,
        availability: user?.role === 'TRADESPERSON' ? formData.availability : undefined,
        // Add other fields with default values
        latitude: 0,
        longitude: 0,
        // Note: Verification statuses are typically managed by the system
        idVerified: 'PENDING',
        phoneVerified: 'PENDING',
        insuranceVerified: 'PENDING',
        averageRating: 0,
        totalReviews: 0,
        completionRate: 0,
        responseRate: 0,
      };
      
      // Update profile
      const response = await UserService.updateProfile(profileData);
      
      if (response.data.success) {
        // Upload avatar if selected
        if (avatarFile) {
          await UserService.uploadAvatar(avatarFile);
        }
        
        // Mark user as onboarded
        const updateResponse = await UserService.updateUser(user?.id || '', { 
          isOnboarded: true 
        });
        
        if (updateResponse.data.success) {
          // Update the user in the context
          updateUser({ 
            ...user, 
            isOnboarded: true,
            profile: response.data.data
          });
          
          // Show celebration effect
          const celebration = document.createElement('div');
          celebration.innerHTML = '<div class="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">' +
            '<div class="absolute inset-0 bg-black bg-opacity-20"></div>' +
            '<div class="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-2xl transform transition-all duration-500 scale-95 animate-scale-in">' +
              '<div class="flex justify-center mb-6">' +
                '<div class="flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-r from-green-400 to-teal-500 shadow-lg">' +
                  '<svg class="h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">' +
                    '<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />' +
                  '</svg>' +
                '</div>' +
              '</div>' +
              '<h2 class="text-2xl font-bold text-gray-900 mb-2">Welcome to Tradrfy!</h2>' +
              '<p class="text-gray-600 mb-6">Your profile is all set up and ready to go.</p>' +
              '<div class="flex justify-center space-x-2 mb-6">' +
                '<div class="w-3 h-3 bg-green-400 rounded-full animate-bounce"></div>' +
                '<div class="w-3 h-3 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>' +
                '<div class="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>' +
              '</div>' +
              '<button id="continue-button" class="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg shadow-md hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300">' +
                'Continue to Dashboard' +
              '</button>' +
            '</div>' +
          '</div>';
          document.body.appendChild(celebration);
          
          // Add event listener to continue button
          setTimeout(() => {
            const continueButton = document.getElementById('continue-button');
            if (continueButton) {
              continueButton.addEventListener('click', () => {
                document.body.removeChild(celebration);
                toast.success('Profile setup completed successfully!');
                navigate('/dashboard');
              });
            }
          }, 100);
        } else {
          throw new Error('Failed to update onboarding status');
        }
      } else {
        throw new Error(response.data.error || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Personal Information
  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
          <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Welcome to Tradrfy!</h2>
        <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
          Let's set up your profile to get you started.
        </p>
      </div>
      
      {/* Avatar Upload with enhanced styling */}
      <div className="flex flex-col items-center mb-8">
        <div className="mb-6 relative group">
          {avatarPreview ? (
            <img 
              src={avatarPreview} 
              alt="Profile preview" 
              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-xl transform transition-transform duration-300 group-hover:scale-105 avatar-preview"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center border-4 border-white shadow-xl transform transition-transform duration-300 group-hover:scale-105 avatar-preview">
              <span className="text-4xl text-gray-500">👤</span>
            </div>
          )}
          <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
            <svg className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>
        <label className="cursor-pointer bg-gradient-to-r from-blue-500 to-purple-600 py-3 px-6 border border-transparent rounded-full shadow-sm text-base font-medium text-white hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 group flex items-center">
          <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Upload Photo
          <input
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="sr-only"
          />
        </label>
        <p className="mt-3 text-sm text-gray-500 flex items-center">
          <svg className="mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          This helps others recognize you
        </p>
      </div>
      
      {/* Name Fields */}
      <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
        <div>
          <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            name="firstName"
            id="firstName"
            required
            value={formData.firstName}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
          />
        </div>
        
        <div>
          <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            name="lastName"
            id="lastName"
            required
            value={formData.lastName}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
          />
        </div>
      </div>
      
      {/* Bio */}
      <div>
        <label htmlFor="bio" className="block text-sm font-semibold text-gray-700 mb-2">
          Bio/Description
        </label>
        <textarea
          id="bio"
          name="bio"
          rows={4}
          maxLength={500}
          placeholder="Tell us a little about yourself..."
          value={formData.bio}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
        />
        <div className="flex justify-between mt-2">
          <p className="text-sm text-gray-500">
            Share what makes you unique
          </p>
          <p className="text-sm font-medium text-gray-700">
            {formData.bio.length}/500 characters
          </p>
        </div>
      </div>
    </div>
  );

  // Step 2: Contact Information
  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
          <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Contact Information</h2>
        <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
          How can others reach you?
        </p>
      </div>
      
      {/* Phone Number */}
      <div>
        <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
          Phone Number
        </label>
        <input
          type="tel"
          name="phoneNumber"
          id="phoneNumber"
          placeholder="+44 7700 900123"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
        />
      </div>
      
      {/* Address */}
      <div>
        <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
          Address
        </label>
        <input
          type="text"
          name="address"
          id="address"
          placeholder="123 Main Street"
          value={formData.address}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
        />
      </div>
      
      {/* City and Postcode */}
      <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
        <div>
          <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
            City
          </label>
          <input
            type="text"
            name="city"
            id="city"
            placeholder="London"
            value={formData.city}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
          />
        </div>
        
        <div>
          <label htmlFor="postcode" className="block text-sm font-semibold text-gray-700 mb-2">
            Postcode
          </label>
          <input
            type="text"
            name="postcode"
            id="postcode"
            placeholder="SW1A 1AA"
            value={formData.postcode}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
          />
        </div>
      </div>
    </div>
  );

  // Step 3: Role-Specific Information
  const renderStep3 = () => {
    if (user?.role === 'TRADESPERSON') {
      return (
        <div className="space-y-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
              <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Tradesperson Details</h2>
            <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
              Tell us about your professional background
            </p>
          </div>
          
          {/* Company Name */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              name="companyName"
              id="companyName"
              placeholder="Your Company Ltd"
              value={formData.companyName}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
            />
          </div>
          
          {/* Years of Experience */}
          <div>
            <label htmlFor="yearsExperience" className="block text-sm font-semibold text-gray-700 mb-2">
              Years of Experience
            </label>
            <input
              type="number"
              name="yearsExperience"
              id="yearsExperience"
              min="0"
              max="50"
              placeholder="5"
              value={formData.yearsExperience}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
            />
          </div>
          
          {/* Hourly Rate */}
          <div>
            <label htmlFor="hourlyRate" className="block text-sm font-semibold text-gray-700 mb-2">
              Hourly Rate (£)
            </label>
            <input
              type="number"
              name="hourlyRate"
              id="hourlyRate"
              min="0"
              step="0.01"
              placeholder="45.00"
              value={formData.hourlyRate}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 hover:shadow-md"
            />
          </div>
          
          {/* Insurance Status */}
          <div className="flex items-center">
            <input
              id="isInsured"
              name="isInsured"
              type="checkbox"
              checked={formData.isInsured}
              onChange={handleInputChange}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="isInsured" className="ml-3 block text-sm font-medium text-gray-700">
              I have professional insurance
            </label>
          </div>
        </div>
      );
    } else {
      // HOMEOWNER specific fields
      return (
        <div className="space-y-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
              <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Homeowner Preferences</h2>
            <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
              Help us find the right professionals for your needs
            </p>
          </div>
          
          {/* Property Types */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-4">
              Property Types
            </label>
            <div className="grid grid-cols-2 gap-3">
              {['House', 'Flat', 'Apartment', 'Bungalow', 'Terraced', 'Semi-detached', 'Detached'].map((type) => (
                <div 
                  key={type}
                  onClick={() => {
                    const currentTypes = formData.homeownerPreferences.propertyTypes;
                    const newTypes = currentTypes.includes(type)
                      ? currentTypes.filter(t => t !== type)
                      : [...currentTypes, type];
                    handlePreferencesChange({
                      ...formData.homeownerPreferences,
                      propertyTypes: newTypes
                    });
                  }}
                  className={`property-type-card cursor-pointer rounded-xl border-2 p-4 text-center transition-all duration-200 ${
                    formData.homeownerPreferences.propertyTypes.includes(type)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-gray-900">{type}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Project Types */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-4">
              Project Types
            </label>
            <div className="grid grid-cols-2 gap-3">
              {['Kitchen', 'Bathroom', 'Garden', 'Plumbing', 'Electrical', 'Painting', 'Flooring', 'Roofing'].map((type) => (
                <div 
                  key={type}
                  onClick={() => {
                    const currentTypes = formData.homeownerPreferences.projectTypes;
                    const newTypes = currentTypes.includes(type)
                      ? currentTypes.filter(t => t !== type)
                      : [...currentTypes, type];
                    handlePreferencesChange({
                      ...formData.homeownerPreferences,
                      projectTypes: newTypes
                    });
                  }}
                  className={`project-type-card cursor-pointer rounded-xl border-2 p-4 text-center transition-all duration-200 ${
                    formData.homeownerPreferences.projectTypes.includes(type)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-gray-900">{type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
  };

  // Step 4: Skills & Expertise (TRADESPERSON only) or Service Preferences (HOMEOWNER)
  const renderStep4 = () => {
    if (user?.role === 'TRADESPERSON') {
      return (
        <div className="space-y-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
              <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Skills & Expertise</h2>
            <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
              What services do you specialize in?
            </p>
          </div>
          
          <SkillsAutocomplete 
            value={formData.skillsOffered}
            onChange={handleSkillsChange}
          />
        </div>
      );
    } else {
      // Service Radius for HOMEOWNER
      return (
        <div className="space-y-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
              <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Service Preferences</h2>
            <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
              How far are you willing to travel for services?
            </p>
          </div>
          
          <div className="space-y-6">
            <div>
              <label htmlFor="serviceRadius" className="block text-sm font-semibold text-gray-700 mb-2">
                Service Radius: {formData.serviceRadius} miles
              </label>
              <input
                type="range"
                name="serviceRadius"
                id="serviceRadius"
                min="5"
                max="100"
                step="5"
                value={formData.serviceRadius}
                onChange={handleInputChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5 miles</span>
                <span>100 miles</span>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Radius Information</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      This radius determines how far away professionals can be from your location to show up in search results.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  // Step 5: Availability (TRADESPERSON only) or Final Step (HOMEOWNER)
  const renderStep5 = () => {
    if (user?.role === 'TRADESPERSON') {
      return (
        <div className="space-y-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
              <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 onboarding-title">Availability</h2>
            <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
              When are you available for work?
            </p>
          </div>
          
          <AvailabilityCalendar 
            value={formData.availability}
            onChange={handleAvailabilityChange}
          />
        </div>
      );
    } else {
      return renderFinalStep();
    }
  };

  // Step 6: Final Step (TRADESPERSON only)
  const renderStep6 = () => {
    if (user?.role === 'TRADESPERSON') {
      return renderFinalStep();
    }
    return null;
  };

  // Final Step
  const renderFinalStep = () => (
    <div className="space-y-8 text-center">
      <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-r from-green-400 to-teal-500 mb-6">
        <svg className="h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      
      <h2 className="text-3xl font-bold text-gray-900 onboarding-title">You're Almost Done!</h2>
      <p className="text-lg text-gray-600 max-w-md mx-auto onboarding-subtitle">
        Review your information and complete your profile setup.
      </p>
      
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Summary</h3>
        <div className="text-left space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Name:</span>
            <span className="font-medium">{formData.firstName} {formData.lastName}</span>
          </div>
          {formData.companyName && (
            <div className="flex justify-between">
              <span className="text-gray-600">Company:</span>
              <span className="font-medium">{formData.companyName}</span>
            </div>
          )}
          {formData.phoneNumber && (
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium">{formData.phoneNumber}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Location:</span>
            <span className="font-medium">{formData.city}, {formData.postcode}</span>
          </div>
          {user?.role === 'TRADESPERSON' && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Experience:</span>
                <span className="font-medium">{formData.yearsExperience || '0'} years</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Rate:</span>
                <span className="font-medium">£{formData.hourlyRate || '0'}/hour</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Skills:</span>
                <span className="font-medium">{formData.skillsOffered.length} selected</span>
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-100">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                You can always update your profile information later in your account settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return renderStep1();
    }
  };

  // Step indicators
  const renderStepIndicators = () => (
    <div className="flex justify-center space-x-2 mb-8">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          className={`step-indicator flex items-center justify-center w-10 h-10 rounded-full border-2 ${
            index + 1 === currentStep
              ? 'border-blue-500 bg-blue-500 text-white'
              : index + 1 < currentStep
              ? 'border-green-500 bg-green-500 text-white'
              : 'border-gray-300 text-gray-400'
          }`}
        >
          {index + 1 < currentStep ? (
            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            index + 1
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="onboarding-container max-w-2xl mx-auto">
      <form onSubmit={handleSubmit}>
        <div className="onboarding-card bg-white rounded-3xl shadow-xl p-8">
          {/* Progress bar */}
          <div className="mb-8">
            {renderStepIndicators()}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step content */}
          <div className="animate-fade-in">
            {renderCurrentStep()}
          </div>

          {/* Navigation buttons */}
          <div className="mt-10 flex justify-between navigation-buttons">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`nav-button py-3 px-6 rounded-xl font-medium shadow-sm transition-all duration-200 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
              }`}
            >
              Back
            </button>
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="nav-button py-3 px-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl shadow-md hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
              >
                Continue
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="nav-button py-3 px-6 bg-gradient-to-r from-green-500 to-teal-600 text-white font-medium rounded-xl shadow-md hover:from-green-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Completing...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default OnboardingPage;
